import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

/**
 * GithubSecuritySummary Component
 * Displays GitHub Advanced Security (GHAS), Dependabot, and CodeQL scanning results
 * Provides visibility into code vulnerabilities and dependency security
 */
const GithubSecuritySummary = ({ owner, repo }) => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [securityData, setSecurityData] = useState(null);

  //const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color')?.trim() || '#F63040';
  //const secondaryColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color')?.trim() || '#57565B';

  // Memoize fetchSecurityData to prevent unnecessary re-creation on every render
  const fetchSecurityData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch GitHub security data (CodeQL + Dependabot)
      const response = await apiFetch(`/api/Github/security-summary?owner=${owner}&repo=${repo}`);
      if (!response.ok) {
        // If endpoint doesn't exist yet, return mock structure for UI testing
        setSecurityData({
          codeScanning: {
            open: 0,
            fixed: 0,
            dismissed: 0,
            bySeverity: { critical: 0, high: 0, medium: 0, low: 0, warning: 0, note: 0 }
          },
          dependabot: {
            open: 0,
            fixed: 0,
            dismissed: 0,
            bySeverity: { critical: 0, high: 0, medium: 0, low: 0 }
          },
          secretScanning: {
            open: 0,
            resolved: 0
          }
        });
        setLoading(false);
        return;
      }
      const data = await response.json().catch(() => null);
      if (data) setSecurityData(data);
    } catch (e) {
        Sentry.captureException(e);
      setError(e.message);
      setSecurityData(null);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, owner, repo]);

  useEffect(() => { fetchSecurityData(); }, [fetchSecurityData]);

  // Helper to normalize severity to PascalCase for consistent color matching
  const normalizeSeverity = (severity) => {
    if (!severity) return 'Unknown';
    return severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase();
  };

  // Parse CodeQL alerts by severity
  // Note: .NET API returns PascalCase (CodeScanning, BySeverity)
  const codeScanningBySeverity = useMemo(() => {
    const bySeverity = securityData?.CodeScanning?.BySeverity || securityData?.codeScanning?.bySeverity;
    if (!bySeverity) return [];
    
    return Object.entries(bySeverity)
      .map(([severity, count]) => ({
        severity: normalizeSeverity(severity),
        count
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => {
        const order = ['Critical', 'High', 'Medium', 'Low', 'Warning', 'Note'];
        const aIdx = order.indexOf(a.severity);
        const bIdx = order.indexOf(b.severity);
        return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
      });
  }, [securityData]);

  // Parse Dependabot alerts by severity
  // Note: .NET API returns PascalCase (Dependabot, BySeverity)
  const dependabotBySeverity = useMemo(() => {
    const bySeverity = securityData?.Dependabot?.BySeverity || securityData?.dependabot?.bySeverity;
    if (!bySeverity) return [];
    
    return Object.entries(bySeverity)
      .map(([severity, count]) => ({
        severity: normalizeSeverity(severity),
        count
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => {
        const order = ['Critical', 'High', 'Medium', 'Low'];
        const aIdx = order.indexOf(a.severity);
        const bIdx = order.indexOf(b.severity);
        return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
      });
  }, [securityData]);

  if (loading) return <HexagonLoader />;
  if (error) return <div style={{ color: 'red', padding: 16 }}>{error}</div>;
  if (!securityData) return null;

  // Note: .NET API returns PascalCase properties (CodeScanning, Dependabot, SecretScanning)
  // Support both PascalCase and camelCase for flexibility
  const codeScanning = securityData.CodeScanning || securityData.codeScanning;
  const dependabot = securityData.Dependabot || securityData.dependabot;
  const secretScanning = securityData.SecretScanning || securityData.secretScanning;
  
  const totalCodeScanningOpen = codeScanning?.Open || codeScanning?.open || 0;
  const totalDependabotOpen = dependabot?.Open || dependabot?.open || 0;
  const totalSecretScanningOpen = secretScanning?.Open || secretScanning?.open || 0;
  const criticalCodeIssues = codeScanning?.BySeverity?.Critical || codeScanning?.bySeverity?.critical || 0;
  const criticalDependabotIssues = dependabot?.BySeverity?.Critical || dependabot?.bySeverity?.critical || 0;
  const highCodeIssues = codeScanning?.BySeverity?.High || codeScanning?.bySeverity?.high || 0;
  const highDependabotIssues = dependabot?.BySeverity?.High || dependabot?.bySeverity?.high || 0;
  const mediumCodeIssues = codeScanning?.BySeverity?.Medium || codeScanning?.bySeverity?.medium || 0;
  const mediumDependabotIssues = dependabot?.BySeverity?.Medium || dependabot?.bySeverity?.medium || 0;

  // Calculate security health score (0-100)
  // Uses logarithmic scaling to handle large numbers of alerts gracefully
  // Rewards active remediation effort (resolution rate and volume of fixes)
  const calculateHealthScore = () => {
    let score = 100;

    const totalCritical = criticalCodeIssues + criticalDependabotIssues;
    const totalHigh = highCodeIssues + highDependabotIssues;
    const totalMedium = mediumCodeIssues + mediumDependabotIssues;
    const lowAndOtherIssues = Math.max(0, (totalCodeScanningOpen + totalDependabotOpen) - 
      (totalCritical + totalHigh + totalMedium));

    // Calculate fixed issues for positive contribution
    const totalFixed = (codeScanning?.Fixed || codeScanning?.fixed || 0) + 
      (dependabot?.Fixed || dependabot?.fixed || 0);
    const totalOpen = totalCodeScanningOpen + totalDependabotOpen;
    const resolutionRate = (totalFixed + totalOpen) > 0 ? totalFixed / (totalFixed + totalOpen) : 0;

    // Logarithmic deductions - provides meaningful scores even with many issues
    // Critical: severe impact, each one matters significantly
    if (totalCritical > 0) {
      score -= Math.min(40, 15 + Math.log10(totalCritical) * 15); // 15-40 points for critical
    }

    // High: significant impact but diminishing returns for large counts  
    if (totalHigh > 0) {
      score -= Math.min(25, 8 + Math.log10(totalHigh + 1) * 10); // 8-25 points for high
    }

    // Medium: moderate impact with strong diminishing returns
    if (totalMedium > 0) {
      score -= Math.min(12, 2 + Math.log10(totalMedium + 1) * 5); // 2-12 points for medium
    }

    // Low/other: minimal impact
    if (lowAndOtherIssues > 0) {
      score -= Math.min(8, Math.log10(lowAndOtherIssues + 1) * 2.5); // 0-8 points for low
    }

    // Secret scanning alerts: still critical, but capped
    if (totalSecretScanningOpen > 0) {
      score -= Math.min(30, 15 + Math.log10(totalSecretScanningOpen) * 10); // 15-30 points for secrets
    }

    // Positive contribution from resolution rate (up to 15 bonus points)
    // A high resolution rate shows the team is actively managing vulnerabilities
    score += resolutionRate * 15;

    // Remediation velocity bonus: reward high volume of fixes (up to 5 bonus points)
    // Teams actively fixing many issues should be recognised for their effort
    if (totalFixed > 0 && resolutionRate >= 0.5) {
      score += Math.min(5, Math.log10(totalFixed + 1) * 2.5);
    }

    // Ensure score stays within 0-100 range
    const finalScore = Math.max(0, Math.min(100, Math.round(score)));

    return finalScore;
  };

  const healthScore = calculateHealthScore();

  const severityColors = {
    Critical: '#b71c1c',
    High: '#d32f2f',
    Medium: '#ff9800',
    Low: '#4caf50',
    Warning: '#ff9800',
    Note: '#2196f3'
  };

  const getScoreInterpretation = (score) => {
    if (score == null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 90) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 75) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 60) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 40) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Critical', color: '#d32f2f', icon: '✗' };
  };

  const scoreInterp = getScoreInterpretation(healthScore);

  const llmProxyText = [
    `Repository: ${owner}/${repo}`,
    `Security Health Score: ${healthScore}/100`,
    `CodeQL Alerts: ${totalCodeScanningOpen} open`,
    `Dependabot Alerts: ${totalDependabotOpen} open`,
    `Secret Scanning Alerts: ${totalSecretScanningOpen} open`,
    `Critical Code Issues: ${criticalCodeIssues}`,
    `Critical Dependency Issues: ${criticalDependabotIssues}`,
    `High Severity Issues: ${highCodeIssues + highDependabotIssues}`,
    `Medium Severity Issues: ${mediumCodeIssues + mediumDependabotIssues}`
  ].join('\n');

  return (
    <div className="dashboard-card group-wrapper github-security-summary">
      <div data-llm-proxy style={{ display: 'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop: 0 }}>CapTrac Security Analytics</h2>

      {/* Executive Summary Banner - matching other section formats */}
      <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
        <div className="exec-banner-layout">
          <div className="exec-banner-score">
            <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
              {healthScore != null ? healthScore : '—'}
              <span className="exec-banner-suffix">/ 100</span>
            </div>
            <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
            <div className="exec-banner-sublabel">Repository Security Health</div>
          </div>
          <div className="exec-banner-body">
            <div className="exec-banner-text">
              <strong>What this means:</strong> This repository has <strong>{totalCodeScanningOpen} CodeQL alerts</strong>, <strong>{totalDependabotOpen} Dependabot alerts</strong>, and <strong>{totalSecretScanningOpen} secret scanning alerts</strong>.
              {criticalCodeIssues + criticalDependabotIssues > 0 && <span style={{ color: '#b71c1c', fontWeight: 600 }}> {criticalCodeIssues + criticalDependabotIssues} critical vulnerabilities require immediate remediation.</span>}
              {totalCodeScanningOpen + totalDependabotOpen === 0 && <span style={{ color: '#2e7d32', fontWeight: 600 }}> Excellent - no open vulnerabilities detected.</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="metrics-row">
        <div className="dashboard-card metric-card metric-card--md">
          <div className="metric-label">CodeQL Alerts</div>
          <div className="metric-value" style={{ color: totalCodeScanningOpen > 10 ? '#d32f2f' : totalCodeScanningOpen > 0 ? '#ff9800' : '#2e7d32' }}>
            {totalCodeScanningOpen}
          </div>
          <div className="metric-subtitle">
            {criticalCodeIssues > 0 ? (
              <span style={{ color: '#b71c1c', fontWeight: 600 }}>{criticalCodeIssues} Critical</span>
            ) : 'Open Vulnerabilities'}
          </div>
        </div>

        <div className="dashboard-card metric-card metric-card--md">
          <div className="metric-label">Dependabot Alerts</div>
          <div className="metric-value" style={{ color: totalDependabotOpen > 10 ? '#d32f2f' : totalDependabotOpen > 0 ? '#ff9800' : '#2e7d32' }}>
            {totalDependabotOpen}
          </div>
          <div className="metric-subtitle">
            {criticalDependabotIssues > 0 ? (
              <span style={{ color: '#b71c1c', fontWeight: 600 }}>{criticalDependabotIssues} Critical</span>
            ) : 'Dependency Vulnerabilities'}
          </div>
        </div>

        <div className="dashboard-card metric-card metric-card--md">
          <div className="metric-label">Secret Scanning</div>
          <div className="metric-value" style={{ color: totalSecretScanningOpen > 0 ? '#d32f2f' : '#2e7d32' }}>
            {totalSecretScanningOpen}
          </div>
          <div className="metric-subtitle">Exposed Secrets</div>
        </div>

        <div className="dashboard-card metric-card metric-card--md">
          <div className="metric-label">Fixed Issues</div>
          <div className="metric-value" style={{ color: '#2e7d32' }}>
            {(codeScanning?.Fixed || codeScanning?.fixed || 0) + (dependabot?.Fixed || dependabot?.fixed || 0)}
          </div>
          <div className="metric-subtitle">Resolved Vulnerabilities</div>
        </div>
      </div>

      {/* Resolution Metrics Section */}
      <div className="dashboard-card" style={{ marginBottom: 16, padding: 16, background: '#f0f7ff', borderLeft: '4px solid #2196f3' }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: 15, color: '#333' }}>📊 Issue Resolution Metrics</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600, marginBottom: 4 }}>Total Fixed</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#2e7d32' }}>
              {(codeScanning?.Fixed || codeScanning?.fixed || 0) + (dependabot?.Fixed || dependabot?.fixed || 0)}
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>Issues resolved (30d)</div>
          </div>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600, marginBottom: 4 }}>Open Issues</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: totalCodeScanningOpen + totalDependabotOpen > 10 ? '#d32f2f' : '#ff9800' }}>
              {totalCodeScanningOpen + totalDependabotOpen}
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>Active vulnerabilities</div>
          </div>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600, marginBottom: 4 }}>Resolution Rate</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#2196f3' }}>
              {((codeScanning?.Fixed || codeScanning?.fixed || 0) + (dependabot?.Fixed || dependabot?.fixed || 0)) > 0 ? 
                (((codeScanning?.Fixed || codeScanning?.fixed || 0) + (dependabot?.Fixed || dependabot?.fixed || 0)) / 
                 ((codeScanning?.Fixed || codeScanning?.fixed || 0) + (dependabot?.Fixed || dependabot?.fixed || 0) + totalCodeScanningOpen + totalDependabotOpen) * 100).toFixed(0) : 0}%
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>Overall fix rate</div>
          </div>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#666', fontWeight: 600, marginBottom: 4 }}>Dismissed</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#9e9e9e' }}>
              {(securityData.CodeScanning?.Dismissed || securityData.codeScanning?.dismissed || 0) +
               (securityData.Dependabot?.Dismissed || securityData.dependabot?.dismissed || 0)}
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>False positives/Accepted risk</div>
          </div>
        </div>
        <div style={{ marginTop: 12, padding: 10, background: '#fff', borderRadius: 4, fontSize: 12, color: '#555', lineHeight: 1.6 }}>
          <strong>Resolution Performance:</strong> The team has successfully resolved {((codeScanning?.Fixed || codeScanning?.fixed || 0) + (dependabot?.Fixed || dependabot?.fixed || 0))} vulnerabilities 
          in the past 30 days with a {(((codeScanning?.Fixed || codeScanning?.fixed || 0) + (dependabot?.Fixed || dependabot?.fixed || 0)) > 0) ? 
            (((codeScanning?.Fixed || codeScanning?.fixed || 0) + (dependabot?.Fixed || dependabot?.fixed || 0)) / 
             ((codeScanning?.Fixed || codeScanning?.fixed || 0) + (dependabot?.Fixed || dependabot?.fixed || 0) + totalCodeScanningOpen + totalDependabotOpen) * 100).toFixed(0) : 0}% resolution rate.
          {criticalCodeIssues + criticalDependabotIssues > 0 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> {criticalCodeIssues + criticalDependabotIssues} critical issues require immediate attention to maintain security posture.</span>}
        </div>
      </div>

      {/* CodeQL and Dependabot Charts */}
      <div className="dashboard-grid compact">
        {/* CodeQL Alerts by Severity */}
        {codeScanningBySeverity.length > 0 && (
          <div className="dashboard-card" style={{ marginBottom: 0, padding: 14, flex: '1 1 280px' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>CodeQL Alerts by Severity</h4>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>Code security vulnerabilities detected</div>
            <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={180} minHeight={180}>
              <BarChart data={codeScanningBySeverity} layout="vertical" margin={{ left: 20, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="severity" type="category" width={80} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} isAnimationActive={!isPrintMode}>
                  {codeScanningBySeverity.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={severityColors[entry.severity] || '#999'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Dependabot Alerts by Severity */}
        {dependabotBySeverity.length > 0 && (
          <div className="dashboard-card" style={{ marginBottom: 0, padding: 14, flex: '1 1 280px' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Dependabot Alerts by Severity</h4>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>Dependency vulnerabilities by risk level</div>
            <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={180} minHeight={180}>
              <PieChart>
                <Pie
                  data={dependabotBySeverity}
                  dataKey="count"
                  nameKey="severity"
                  cx="50%"
                  cy="50%"
                  outerRadius={60}
                  label={(entry) => `${entry.severity}: ${entry.count}`}
                  labelLine={true}
                  isAnimationActive={!isPrintMode}
                >
                  {dependabotBySeverity.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={severityColors[entry.severity] || '#999'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Security Impact Analysis */}
      {(totalCodeScanningOpen > 0 || totalDependabotOpen > 0) && (
        <div className="dashboard-card" style={{ marginTop: 16, padding: 16, background: criticalCodeIssues + criticalDependabotIssues > 0 ? '#fff3e0' : '#f5f5f5' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>⚠️ Security Impact Analysis</h4>
          <div style={{ fontSize: 12, color: '#555', lineHeight: 1.7 }}>
            <strong>Priority Actions Required:</strong>
            <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
              {criticalCodeIssues > 0 && (
                <li style={{ color: '#b71c1c', fontWeight: 600, marginBottom: 4 }}>
                  {criticalCodeIssues} Critical CodeQL alert{criticalCodeIssues !== 1 ? 's' : ''} - These represent severe security vulnerabilities that could lead to data breaches or system compromise. Immediate remediation required.
                </li>
              )}
              {criticalDependabotIssues > 0 && (
                <li style={{ color: '#b71c1c', fontWeight: 600, marginBottom: 4 }}>
                  {criticalDependabotIssues} Critical Dependabot alert{criticalDependabotIssues !== 1 ? 's' : ''} - Dependencies with known exploits actively used in the wild. Update or patch immediately.
                </li>
              )}
              {highCodeIssues > 0 && (
                <li style={{ color: '#d32f2f', marginBottom: 4 }}>
                  {highCodeIssues} High severity CodeQL alert{highCodeIssues !== 1 ? 's' : ''} - Should be addressed within 7 days to prevent potential security incidents.
                </li>
              )}
              {highDependabotIssues > 0 && (
                <li style={{ color: '#d32f2f', marginBottom: 4 }}>
                  {highDependabotIssues} High severity Dependabot alert{highDependabotIssues !== 1 ? 's' : ''} - Known vulnerabilities with proof-of-concept exploits available. Prioritize updates.
                </li>
              )}
              {totalCodeScanningOpen + totalDependabotOpen - criticalCodeIssues - criticalDependabotIssues - highCodeIssues - highDependabotIssues > 0 && (
                <li style={{ color: '#ff9800', marginBottom: 4 }}>
                  {totalCodeScanningOpen + totalDependabotOpen - criticalCodeIssues - criticalDependabotIssues - highCodeIssues - highDependabotIssues} Medium/Low severity alert{(totalCodeScanningOpen + totalDependabotOpen - criticalCodeIssues - criticalDependabotIssues - highCodeIssues - highDependabotIssues) !== 1 ? 's' : ''} - Address in regular security maintenance windows.
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* No vulnerabilities message */}
      {codeScanningBySeverity.length === 0 && dependabotBySeverity.length === 0 && totalSecretScanningOpen === 0 && (
        <div className="dashboard-card" style={{ padding: 24, textAlign: 'center', background: '#e8f5e9', marginTop: 16 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#2e7d32', marginBottom: 8 }}>
            Excellent Security Posture
          </div>
          <div style={{ fontSize: 13, color: '#666' }}>
            No code vulnerabilities, dependency issues, or exposed secrets detected in this repository.
          </div>
        </div>
      )}

      {/* Security Best Practices & Recommendations */}
      <div style={{ fontSize: 12, color: '#666', marginTop: 16, padding: 14, background: '#f8f9fa', borderRadius: 4, lineHeight: 1.7 }}>
        <strong style={{ fontSize: 13, color: '#333' }}>🎯 GitHub Advanced Security Recommendations:</strong>
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          <div>
            <strong style={{ display: 'block', marginBottom: 6, color: '#2196f3' }}>🔍 CodeQL Scanning</strong>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 11 }}>
              <li>Runs automatically on push and pull requests</li>
              <li>Review critical/high alerts within 24-48 hours</li>
              <li>Enable additional query suites for comprehensive coverage</li>
              <li>Integrate findings into IDE for early detection</li>
            </ul>
          </div>
          <div>
            <strong style={{ display: 'block', marginBottom: 6, color: '#4caf50' }}>📦 Dependabot</strong>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 11 }}>
              <li>Automatic dependency vulnerability scanning</li>
              <li>Enable auto-merge for low-risk security patches</li>
              <li>Review and update dependencies weekly</li>
              <li>Monitor for supply chain attack indicators</li>
            </ul>
          </div>
          <div>
            <strong style={{ display: 'block', marginBottom: 6, color: '#ff9800' }}>🔐 Secret Scanning</strong>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 11 }}>
              <li>Detects exposed credentials and API keys</li>
              <li>Immediately revoke and rotate detected secrets</li>
              <li>Use secret management services (Azure Key Vault)</li>
              <li>Enable push protection to prevent secret commits</li>
            </ul>
          </div>
          <div>
            <strong style={{ display: 'block', marginBottom: 6, color: '#9c27b0' }}>🛡️ Additional Measures</strong>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 11 }}>
              <li>Require security checks before merging PRs</li>
              <li>Create private security advisories for CVEs</li>
              <li>Enable dependency review for pull requests</li>
              <li>Regular security training for development team</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GithubSecuritySummary;
